import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

function HIPAAAuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [chainResult, setChainResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [filters, setFilters] = useState({
    event_type: '',
    resource_type: '',
    sensitivity_level: '',
    start_date: '',
    end_date: '',
    q: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    eventTypes: [],
    resourceTypes: [],
    sensitivityLevels: ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'],
  });
  const [pagination, setPagination] = useState({ limit: 50, offset: 0, total: 0 });

  useEffect(() => { loadFilterOptions(); }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: pagination.limit, offset: pagination.offset };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });

      const response = await api.getHIPAAAuditLogs(params);
      setLogs(response.data || []);
      setPagination(prev => ({ ...prev, total: response.pagination?.total || 0 }));
    } catch (err) {
      setError('Failed to load HIPAA audit logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.offset]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const loadFilterOptions = async () => {
    try {
      const response = await api.getHIPAAAuditLogFilters();
      if (response.data) setFilterOptions(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({ event_type: '', resource_type: '', sensitivity_level: '', start_date: '', end_date: '', q: '' });
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleVerifyChain = async () => {
    try {
      setVerifying(true);
      setChainResult(null);
      const response = await api.verifyAuditChain({});
      setChainResult(response.data);
    } catch (err) {
      setChainResult({ valid: false, error: err.message });
    } finally {
      setVerifying(false);
    }
  };

  const handleExport = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const url = api.getHIPAAAuditExportURL(params);
    const token = localStorage.getItem('nh_auth_token');
    // Open with auth header via fetch + blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `hipaa-audit-log-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
      })
      .catch(err => console.error('Export failed:', err));
  };

  const handleViewDetails = async (logId) => {
    try {
      const response = await api.getHIPAAAuditLogDetail(logId);
      setSelectedLog(response.data);
    } catch (err) {
      console.error('Failed to load log detail:', err);
    }
  };

  const getSensitivityColor = (level) => {
    switch (level) {
      case 'LOW': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'MEDIUM': return { bg: '#fff3e0', color: '#e65100' };
      case 'HIGH': return { bg: '#fbe9e7', color: '#bf360c' };
      case 'CRITICAL': return { bg: '#fce4ec', color: '#b71c1c' };
      default: return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  const getEventTypeColor = (eventType) => {
    if (eventType.includes('LOGIN') || eventType.includes('LOGOUT') || eventType.includes('PASSWORD')) return '#fff3cd';
    if (eventType.includes('CREATED')) return '#d4edda';
    if (eventType.includes('UPDATED') || eventType.includes('CHANGED')) return '#d1ecf1';
    if (eventType.includes('DELETED')) return '#f8d7da';
    if (eventType.includes('VIEWED') || eventType.includes('SEARCHED')) return '#e2e3e5';
    if (eventType.includes('SENT') || eventType.includes('CALL')) return '#d6d8f8';
    if (eventType.includes('CONSENT')) return '#fff0e6';
    if (eventType.includes('AUDIT')) return '#e8eaf6';
    return '#f5f5f5';
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div>
      <div className="card">
        <div style={styles.header}>
          <div>
            <h2 style={{ margin: 0 }}>HIPAA Audit Log</h2>
            <p style={{ color: '#666', margin: '4px 0 0' }}>
              Immutable, hash-chained audit trail for all PHI access and system activity.
            </p>
          </div>
          <div style={styles.headerActions}>
            <button className="button secondary" onClick={handleVerifyChain} disabled={verifying}>
              {verifying ? 'Verifying...' : 'Verify Chain'}
            </button>
            <button className="button secondary" onClick={handleExport}>
              Export CSV
            </button>
          </div>
        </div>

        {/* Chain verification result */}
        {chainResult && (
          <div style={{
            ...styles.chainResult,
            background: chainResult.valid ? '#e8f5e9' : '#fce4ec',
            borderColor: chainResult.valid ? '#4caf50' : '#f44336'
          }}>
            <strong>{chainResult.valid ? 'Chain Integrity: PASSED' : 'Chain Integrity: FAILED'}</strong>
            <span style={{ marginLeft: 12 }}>
              {chainResult.logsVerified} logs verified
              {chainResult.brokenAt && ` — broken at sequence ${chainResult.brokenAt}`}
            </span>
            <button onClick={() => setChainResult(null)} style={styles.dismissBtn}>Dismiss</button>
          </div>
        )}

        {/* Filters */}
        <div style={styles.filterContainer}>
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Search</label>
              <input
                type="text"
                placeholder="Search actions, users..."
                value={filters.q}
                onChange={(e) => handleFilterChange('q', e.target.value)}
                style={styles.filterInput}
              />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Event Type</label>
              <select value={filters.event_type} onChange={(e) => handleFilterChange('event_type', e.target.value)} style={styles.filterSelect}>
                <option value="">All Events</option>
                {filterOptions.eventTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Resource</label>
              <select value={filters.resource_type} onChange={(e) => handleFilterChange('resource_type', e.target.value)} style={styles.filterSelect}>
                <option value="">All Resources</option>
                {filterOptions.resourceTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Sensitivity</label>
              <select value={filters.sensitivity_level} onChange={(e) => handleFilterChange('sensitivity_level', e.target.value)} style={styles.filterSelect}>
                <option value="">All Levels</option>
                {filterOptions.sensitivityLevels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>From</label>
              <input type="date" value={filters.start_date} onChange={(e) => handleFilterChange('start_date', e.target.value)} style={styles.filterInput} />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>To</label>
              <input type="date" value={filters.end_date} onChange={(e) => handleFilterChange('end_date', e.target.value)} style={styles.filterInput} />
            </div>
            <button className="button secondary" onClick={handleClearFilters} style={{ alignSelf: 'flex-end' }}>
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="loading">Loading audit logs...</div>
        ) : error ? (
          <div style={{ color: '#c62828', padding: 16 }}>{error}</div>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Event</th>
                  <th>Resource</th>
                  <th>Sensitivity</th>
                  <th>PHI</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#666' }}>No audit log entries found.</td></tr>
                ) : logs.map(log => {
                  const sensColor = getSensitivityColor(log.sensitivity_level);
                  return (
                    <tr key={log.id}>
                      <td style={styles.timestamp}>{new Date(log.event_timestamp).toLocaleString()}</td>
                      <td>
                        <div>{log.user_first_name} {log.user_last_name}</div>
                        <div style={styles.subText}>{log.user_email}</div>
                      </td>
                      <td>
                        <span style={{ ...styles.badge, background: getEventTypeColor(log.event_type) }}>
                          {log.event_type.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>
                        <div>{log.resource_type || '-'}</div>
                        {log.resource_id && <div style={styles.subText}>ID: {log.resource_id}</div>}
                        {log.resource_name && <div style={styles.subText}>{log.resource_name}</div>}
                      </td>
                      <td>
                        <span style={{ ...styles.badge, background: sensColor.bg, color: sensColor.color }}>
                          {log.sensitivity_level}
                        </span>
                      </td>
                      <td>{log.contains_phi ? 'Yes' : 'No'}</td>
                      <td>
                        <button className="button secondary" style={{ padding: '4px 8px', fontSize: 12 }} onClick={() => handleViewDetails(log.id)}>
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div style={styles.pagination}>
              <span>Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}</span>
              <div style={styles.paginationButtons}>
                <button className="button secondary" onClick={() => setPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))} disabled={pagination.offset === 0}>Previous</button>
                <span style={styles.pageInfo}>Page {currentPage} of {totalPages || 1}</span>
                <button className="button secondary" onClick={() => setPagination(p => ({ ...p, offset: p.offset + p.limit }))} disabled={pagination.offset + pagination.limit >= pagination.total}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div style={styles.modalOverlay} onClick={() => setSelectedLog(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>Audit Log Detail</h3>
              <button onClick={() => setSelectedLog(null)} style={styles.closeBtn}>X</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.detailGrid}>
                <DetailRow label="Log ID" value={selectedLog.id} />
                <DetailRow label="GUID" value={selectedLog.guid} />
                <DetailRow label="Sequence" value={selectedLog.log_sequence} />
                <DetailRow label="Timestamp" value={new Date(selectedLog.event_timestamp).toLocaleString()} />
                <DetailRow label="Event Type" value={selectedLog.event_type} />
                <DetailRow label="Action" value={selectedLog.action} />
                <DetailRow label="User" value={`${selectedLog.user_first_name || ''} ${selectedLog.user_last_name || ''} (${selectedLog.user_email || 'System'})`} />
                <DetailRow label="Role" value={selectedLog.user_role} />
                <DetailRow label="Resource" value={`${selectedLog.resource_type || '-'} ${selectedLog.resource_id || ''}`} />
                <DetailRow label="Resource Name" value={selectedLog.resource_name} />
                <DetailRow label="IP Address" value={selectedLog.ip_address} />
                <DetailRow label="Sensitivity" value={selectedLog.sensitivity_level} />
                <DetailRow label="Contains PHI" value={selectedLog.contains_phi ? 'Yes' : 'No'} />
                <DetailRow label="Retention Until" value={selectedLog.retention_until} />
              </div>

              {/* Hash Chain Info */}
              <div style={styles.detailSection}>
                <h4 style={styles.sectionTitle}>Hash Chain</h4>
                <div style={styles.hashInfo}>
                  <div><strong>Hash:</strong> <code style={styles.code}>{selectedLog.log_hash}</code></div>
                  <div><strong>Previous:</strong> <code style={styles.code}>{selectedLog.previous_log_hash}</code></div>
                </div>
              </div>

              {/* Change Tracking */}
              {(selectedLog.old_values || selectedLog.new_values) && (
                <div style={styles.detailSection}>
                  <h4 style={styles.sectionTitle}>Changes</h4>
                  {selectedLog.change_summary && <p style={{ margin: '0 0 8px', color: '#666' }}>{selectedLog.change_summary}</p>}
                  <div style={styles.diffContainer}>
                    {selectedLog.old_values && (
                      <div style={styles.diffBlock}>
                        <div style={styles.diffLabel}>Before</div>
                        <pre style={styles.pre}>{formatJSON(selectedLog.old_values)}</pre>
                      </div>
                    )}
                    {selectedLog.new_values && (
                      <div style={styles.diffBlock}>
                        <div style={styles.diffLabel}>After</div>
                        <pre style={styles.pre}>{formatJSON(selectedLog.new_values)}</pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Metadata */}
              {selectedLog.metadata && (
                <div style={styles.detailSection}>
                  <h4 style={styles.sectionTitle}>Metadata</h4>
                  <pre style={styles.pre}>{formatJSON(selectedLog.metadata)}</pre>
                </div>
              )}

              {selectedLog.reason && (
                <div style={styles.detailSection}>
                  <h4 style={styles.sectionTitle}>Reason</h4>
                  <p>{selectedLog.reason}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={styles.detailRow}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={styles.detailValue}>{String(value)}</span>
    </div>
  );
}

function formatJSON(val) {
  try {
    const parsed = typeof val === 'string' ? JSON.parse(val) : val;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(val);
  }
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  headerActions: { display: 'flex', gap: 8 },
  chainResult: { padding: '10px 16px', borderRadius: 6, border: '1px solid', marginBottom: 16, display: 'flex', alignItems: 'center', fontSize: 14 },
  dismissBtn: { marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#666', fontSize: 12 },
  filterContainer: { background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 20 },
  filterRow: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', minWidth: 130 },
  filterLabel: { fontSize: 12, fontWeight: 500, color: '#666', marginBottom: 4 },
  filterSelect: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14 },
  filterInput: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14 },
  timestamp: { whiteSpace: 'nowrap', fontSize: 13 },
  subText: { fontSize: 12, color: '#666' },
  badge: { display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, padding: '10px 0', borderTop: '1px solid #eee' },
  paginationButtons: { display: 'flex', alignItems: 'center', gap: 10 },
  pageInfo: { color: '#666', fontSize: 14 },
  // Modal
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 60, zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 8, width: '90%', maxWidth: 800, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #eee' },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#666' },
  modalBody: { padding: 24 },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 },
  detailRow: { display: 'flex', flexDirection: 'column', padding: '6px 0' },
  detailLabel: { fontSize: 11, fontWeight: 600, color: '#666', textTransform: 'uppercase', marginBottom: 2 },
  detailValue: { fontSize: 14, color: '#333', wordBreak: 'break-all' },
  detailSection: { marginTop: 20, borderTop: '1px solid #eee', paddingTop: 16 },
  sectionTitle: { margin: '0 0 8px', fontSize: 14, fontWeight: 600 },
  hashInfo: { fontFamily: 'monospace', fontSize: 12, background: '#f5f5f5', padding: 12, borderRadius: 4, wordBreak: 'break-all' },
  code: { background: '#f0f0f0', padding: '2px 4px', borderRadius: 2, fontSize: 11, wordBreak: 'break-all' },
  diffContainer: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  diffBlock: { background: '#f8f9fa', borderRadius: 4, overflow: 'hidden' },
  diffLabel: { padding: '6px 12px', fontSize: 12, fontWeight: 600, background: '#e9ecef' },
  pre: { margin: 0, padding: 12, fontSize: 12, overflow: 'auto', maxHeight: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
};

export default HIPAAAuditLog;
